import React, { useState, useEffect } from "react";
import { ProSidebar, Menu, MenuItem, SubMenu } from "react-pro-sidebar";
import { Box, IconButton, Typography, useTheme, useMediaQuery } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import "react-pro-sidebar/dist/css/styles.css";
import { tokens } from "../../theme";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import RealEstateIcon from "@mui/icons-material/Apartment";
import AgricultureOutlinedIcon from '@mui/icons-material/AgricultureOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import { useAuth } from "../../auth/Login";
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';

const Item = ({ title, to, icon, isCollapsed }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const location = useLocation();
  const isActive = location.pathname === to;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <MenuItem
      active={isActive}
      style={{ 
        color: colors.grey[100],
        padding: isCollapsed && isMobile ? '8px 5px' : undefined
      }}
      icon={icon}
    >
      <Typography variant={isMobile ? "body2" : "body1"} style={{ fontSize: isMobile ? "0.95rem" : "1.1rem" }}>{title}</Typography>
      <Link to={to} style={{ all: "unset", display: "block", width: "100%", height: "100%" }} />
    </MenuItem>
  );
};

const Sidebar = ({ isSidebarOpen, setIsSidebarOpen }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { user } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  // Auto-collapse the internal content on mobile and tablet screens
  useEffect(() => {
    if (isMobile || isTablet) {
      setIsCollapsed(true);
    } else {
      setIsCollapsed(false);
    }
  }, [isMobile, isTablet]);

  // Handle closing the sidebar when a menu item is clicked on mobile
  const handleMenuItemClick = () => {
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  };

  return (
    <Box
      sx={{
        "& .pro-sidebar-inner": {
          background: `${colors.grey[800]} !important`,
        },
        "& .pro-icon-wrapper": {
          backgroundColor: "transparent !important",
        },
        "& .pro-inner-item": {
          padding: isMobile ? "2px 15px 2px 10px !important" : "5px 35px 5px 20px !important",
          fontSize: isMobile ? "0.95rem" : "1.1rem",
          transition: "all 0.2s ease-in-out",
        },
        "& .pro-inner-item:hover": {
          color: `#ffffff !important`,
          backgroundColor: `${colors.grey[600]} !important`,
          borderRadius: "4px !important",
          transform: "translateX(5px)",
          boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
        },
        "& .pro-menu-item.active": {
          color: `${colors.grey[100]} !important`,
          fontWeight: "bold !important",
          "& .pro-inner-item": {
            backgroundColor: `${colors.grey[700]} !important`,
            borderLeft: `4px solid ${colors.grey[100]} !important`,
            borderRadius: "4px !important",
          },
          "& .pro-icon-wrapper": {
            color: `${colors.grey[100]} !important`,
          },
        },
        "& .pro-menu": {
          fontSize: isMobile ? "0.95rem" : "1.1rem",
        },
        "& .pro-menu-item": {
          margin: "4px 0",
        },
        "& .pro-sub-menu-content": {
          backgroundColor: `${colors.grey[700]} !important`,
          borderRadius: "4px !important",
          margin: "0 10px !important",
          boxShadow: "0 4px 8px rgba(0,0,0,0.2) !important",
        },
        "& .pro-sub-menu:hover": {
          backgroundColor: "transparent !important", 
        },
        "& .pro-sub-menu-content .pro-inner-item:hover": {
          backgroundColor: `${colors.grey[600]} !important`,
        },
        "& .pro-sidebar .pro-menu .pro-menu-item > .pro-inner-item:focus": {
          color: `${colors.grey[100]} !important`,
          backgroundColor: `${colors.grey[700]} !important`,
        },
        height: '100%',
        "& .pro-sidebar": {
          height: '100%',
          minWidth: isCollapsed ? (isMobile ? "60px" : "80px") : "250px !important",
          width: isCollapsed ? (isMobile ? "60px" : "80px") : "250px !important",
        },
        "& .pro-sidebar.collapsed": {
          width: isMobile ? "60px !important" : "80px !important",
          minWidth: isMobile ? "60px !important" : "80px !important",
        },
        "& .pro-sidebar-footer": {
          padding: isMobile ? "8px" : "20px",
        },
        "& .pro-menu-item": {
          fontSize: isMobile ? "0.95rem" : "1.1rem",
        }
      }}
      onClick={handleMenuItemClick}
    >
      <ProSidebar collapsed={isCollapsed}>
        <Menu iconShape="square">
          <MenuItem
            onClick={(e) => {
              e.stopPropagation(); // Prevent sidebar from closing
              setIsCollapsed(!isCollapsed);
            }}
            icon={isCollapsed ? <MenuOutlinedIcon /> : undefined}
            style={{
              margin: isMobile ? "5px 0 10px 0" : "10px 0 20px 0",
              color: colors.grey[100],
            }}
          >
            {!isCollapsed && (
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                ml="15px"
              >
                <Box display="flex" alignItems="center">
                  <img 
                    src="/assets/Logo.webp" 
                    alt="JvLands Logo" 
                    style={{ 
                      height: isMobile ? "30px" : "40px",
                      marginRight: "10px"
                    }}
                  />
                </Box>
                <IconButton 
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent sidebar from closing
                    setIsCollapsed(!isCollapsed);
                  }}
                  size={isMobile ? "small" : "medium"}
                >
                  <MenuOutlinedIcon />
                </IconButton>
              </Box>
            )}
          </MenuItem>

          {!isCollapsed && (
            <Box mb="25px">
              <Box display="flex" justifyContent="center" alignItems="center">
                <img
                  alt="profile-user"
                  width={isMobile ? "60px" : "100px"}
                  height={isMobile ? "60px" : "100px"}
                  src="/assets/9440461.jpg"
                  style={{ cursor: "pointer", borderRadius: "50%" }}
                />
              </Box>
              <Box textAlign="center">
                <Typography
                  variant={isMobile ? "h5" : "h2"}
                  color={colors.grey[100]}
                  fontWeight="bold"
                  sx={{ m: "10px 0 0 0" }}
                >
                  {user?.Username || "User"}
                </Typography>
                <Typography 
                  variant={isMobile ? "body2" : "h5"} 
                  color={colors.blueAccent[300]}
                  fontWeight="bold"
                >
                  {user?.Role?.Name || "User"}
                </Typography>
              </Box>
            </Box>
          )}

          <Box paddingLeft={isCollapsed ? undefined : "10%"}>
            <Item
              title="Dashboard"
              to="/dashboard"
              icon={<HomeOutlinedIcon fontSize={isMobile ? "small" : "medium"} />}
              isCollapsed={isCollapsed}
            />
            
            {/* Show menu items based on user's Actions */}
            {user?.Actions?.map(action => {
              switch(action.Name.toLowerCase()) {
                case 'property listing':
                  return (
                    <React.Fragment key={action.Action_ID}>
                      <SubMenu 
                        title="Property Listing" 
                        icon={<ListAltIcon fontSize={isMobile ? "small" : "medium"} />}
                        style={{ color: colors.grey[100], fontSize: isMobile ? "0.95rem" : "1.1rem" }}
                      >
                        <Item
                          title="Rent"
                          to="/listing/rent"
                          icon={<MeetingRoomOutlinedIcon fontSize={isMobile ? "small" : "medium"} />}
                          isCollapsed={isCollapsed}
                        />
                        <Item
                          title="Sell"
                          to="/listing/sell"
                          icon={<SellOutlinedIcon fontSize={isMobile ? "small" : "medium"} />}
                          isCollapsed={isCollapsed}
                        />
                        {/* Add Investor Listing link only for Admin */}
                        {user?.Role?.Name === "Admin" && (
                            <Item
                                title="Investor"
                                to="/listing/invest"
                                icon={<AccountBalanceOutlinedIcon fontSize={isMobile ? "small" : "medium"} />}
                                isCollapsed={isCollapsed}
                            />
                        )}
                        <Item
                          title="Joint Venture"
                          to="/listing/jointventure"
                          icon={<HandshakeOutlinedIcon fontSize={isMobile ? "small" : "medium"} />}
                          isCollapsed={isCollapsed}
                        />
                        <Item
                          title="Agriculture"
                          to="/listing/agriculture"
                          icon={<AgricultureOutlinedIcon fontSize={isMobile ? "small" : "medium"} />}
                          isCollapsed={isCollapsed}
                        />
                      </SubMenu>
                    </React.Fragment>
                  );

                case 'property leads':
                  return (
                    <React.Fragment key={action.Action_ID}>
                      <SubMenu 
                        title="Property Leads" 
                        icon={<ListAltIcon fontSize={isMobile ? "small" : "medium"} />}
                        style={{ color: colors.grey[100], fontSize: isMobile ? "0.95rem" : "1.1rem" }}
                      >
                        <Item
                          title="Rent"
                          to="/rent"
                          icon={<MeetingRoomOutlinedIcon fontSize={isMobile ? "small" : "medium"} />}
                          isCollapsed={isCollapsed}
                        />
                        <Item
                          title="Sell"
                          to="/sell"
                          icon={<SellOutlinedIcon fontSize={isMobile ? "small" : "medium"} />}
                          isCollapsed={isCollapsed}
                        />
                        <Item
                          title="Invest"
                          to="/invest"
                          icon={<AccountBalanceOutlinedIcon fontSize={isMobile ? "small" : "medium"} />}
                          isCollapsed={isCollapsed}
                        />
                        <Item
                          title="Joint Venture"
                          to="/jointventure"
                          icon={<HandshakeOutlinedIcon fontSize={isMobile ? "small" : "medium"} />}
                          isCollapsed={isCollapsed}
                        />
                        <Item
                          title="Agriculture Yearly Contract"
                          to="/agriculture/yearly-contract"
                          icon={<AgricultureOutlinedIcon fontSize={isMobile ? "small" : "medium"} />}
                          isCollapsed={isCollapsed}
                        />
                        <Item
                          title="Agriculture Sell"
                          to="/agriculture/sell"
                          icon={<SellOutlinedIcon fontSize={isMobile ? "small" : "medium"} />}
                          isCollapsed={isCollapsed}
                        />
                      </SubMenu>
                    </React.Fragment>
                  );
                
                case 'documents verification':
                  return (
                    <Item
                      key={action.Action_ID}
                      title="Documents Verification"
                      to="/documents-verification"
                      icon={<VerifiedUserOutlinedIcon fontSize={isMobile ? "small" : "medium"} />}
                      isCollapsed={isCollapsed}
                    />
                  );
                
                case 'property issues':
                  return (
                    <Item
                      key={action.Action_ID}
                      title="Property Issues"
                      to="/propertyissues"
                      icon={<ReportProblemOutlinedIcon fontSize={isMobile ? "small" : "medium"} />}
                      isCollapsed={isCollapsed}
                    />
                  );
                
                case 'agents':
                  return (
                    <Item
                      key={action.Action_ID}
                      title="Agents"
                      to="/agents"
                      icon={<PeopleOutlinedIcon fontSize={isMobile ? "small" : "medium"} />}
                      isCollapsed={isCollapsed}
                    />
                  );
                
                default:
                  return null;
              }
            })}

            {user?.Role?.Name === "Admin" && (
              <SubMenu
                title="Property Issue"
                icon={<ApartmentOutlinedIcon />}
                style={{ color: colors.grey[100] }}
              >
                <Item
                  title="Appointments"
                  to="/appointments"
                  icon={<CalendarMonthOutlinedIcon />}
                  isCollapsed={isCollapsed}
                />
                <Item
                  title="Property Questions"
                  to="/property-questions"
                  icon={<QuestionAnswerOutlinedIcon />}
                  isCollapsed={isCollapsed}
                />
              </SubMenu>
            )}

            {isMobile && (
              <MenuItem
                icon={<CloseOutlinedIcon fontSize="small" />}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsSidebarOpen(false);
                }}
                style={{
                  marginTop: "20px",
                  color: colors.grey[300],
                  padding: "8px 5px"
                }}
              >
                <Typography variant="body2" style={{ fontSize: "0.95rem" }}>Close Menu</Typography>
              </MenuItem>
            )}
          </Box>
        </Menu>
      </ProSidebar>
    </Box>
  );
};

export default Sidebar;